import { useUser } from '@/domain/user/hooks'
import { useIntl } from '@/intl'
import { useSetActiveTab } from '@/scopes/account/group-responsible/domain/hooks'
import groupResponsibleUseCases from '@/scopes/account/group-responsible/domain/useCases'
import groupResponsibleAPIClient from '@/scopes/account/group-responsible/infrastructure/groupResponsibleAPIClient'
import { groupResponsibleStore } from '@/scopes/account/group-responsible/infrastructure/store'
import { useGroupResponsibleStore } from '@/scopes/account/group-responsible/infrastructure/useGroupResponsibleStore'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import QSClient from '@/scopes/common/infrastructure/query-strings/queryStringClient'
import {
  Icon,
  type IconGlyph,
  LoadingSpinner,
  UnavailableContentDialog,
} from '@sunwebgroup/ui-components'
import { tok, tokPx } from '@sunwebgroup/ui-components'
import { Typography } from '@sunwebgroup/ui-components/uds'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as React from 'react'
import { useMount } from 'react-use'
import { styled } from 'styled-components'
import { Header } from '../../../common/components/Header'
import { BusTable } from './Components/BusTable'
import { DownloadButton } from './Components/downloadButton/DownloadButton'
import { Hero } from './Components/Hero'
import { ParticipantTable } from './Components/ParticipantTable'
import { RoomsTable } from './Components/RoomsTable'
import { GroupResponsibleDependenciesProvider, useGroupResponsibleUseCases } from './dependencies'
import { getBusAssignmentDeadLine, isBusTabActive, isParticipantsTabActive, isRoomsTabActive } from './selectors'

interface StatusNotificationProps {
  iconGlyph: IconGlyph
  title: string
  message: string
}

const queryClient = new QueryClient()

const PageLayout = styled.div`
  background-color: ${tok('semantics_views_background_color')};
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`

const TabsWrapper = styled.div`
  max-width: ${tokPx('primitives_area_size_1440')};
  width: 100%;
  margin: ${tokPx('primitives_space_32')} auto;
  padding: 0 ${tokPx('primitives_space_48')};
  color: ${tok('semantics_content_color')};

  @media (max-width: ${tokPx('primitives_breakpoint_m_max')}) {
    padding: 0 ${tokPx('primitives_space_16')};
    margin-top: ${tokPx('primitives_space_16')};
  }
`

const TabTitle = styled(Typography)`
  @media (max-width: ${tokPx('primitives_breakpoint_m_max')}) {
    ${Typography.modVariant('display-e-regular')}
  }
`

const TabSubtitle = styled(Typography)`
  max-width: ${tokPx('primitives_area_size_720')};
  margin-bottom: ${tokPx('primitives_space_16')};
  margin-top: -${tokPx('primitives_space_12')};

`

const CenteredContent = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  margin-bottom: 64px;
`

const TableTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${tokPx('primitives_space_16')};
`

const TableWrapper = styled.div``

const StatusNotification: React.FC<StatusNotificationProps> = ({ iconGlyph, title, message }) => {
  const intl = useIntl()

  return (
    <CenteredContent>
      <UnavailableContentDialog
        icon={<Icon glyph={iconGlyph} size="lg" />}
        title={intl.formatMessage({ id: title })}
        message={intl.formatMessage({ id: message })}
      />
    </CenteredContent>
  )
}

export function GroupsResponsible() {
  const locale = useConfiguration('locale')
  const dependencies = React.useMemo(() => {
    const useCases = groupResponsibleUseCases({
      apiClient: groupResponsibleAPIClient,
      store: groupResponsibleStore,
      queryStringClient: QSClient,
      locale,
    })

    return {
      groupResponsibleAPIClient,
      groupResponsibleStore,
      useCases,
    }
  }, [locale])
  return (
    <GroupResponsibleDependenciesProvider dependencies={dependencies}>
      <QueryClientProvider client={queryClient}>
        <PageLayout>
          <Header hideLiveSearch />
          <Page />
        </PageLayout>
      </QueryClientProvider>
    </GroupResponsibleDependenciesProvider>
  )
}

function Page() {
  const locale = useConfiguration('locale')
  const useCases = useGroupResponsibleUseCases()
  const { isLoggedIn } = useUser()
  useSetActiveTab()
  useMount(() => useCases.pageLoaded())
  const loading = useGroupResponsibleStore(state => state.retrieveBookingData.loading)
  const error = useGroupResponsibleStore(state => state.retrieveBookingData.error)
  const { deadLineDate, daysBefore } = useGroupResponsibleStore(state => getBusAssignmentDeadLine(state, locale))
  const intl = useIntl()
  const activeTabs = {
    participants: useGroupResponsibleStore(isParticipantsTabActive),
    rooms: useGroupResponsibleStore(isRoomsTabActive),
    bus: useGroupResponsibleStore(isBusTabActive),
  }

  if (loading) {
    return <LoadingState />
  }

  if (error) {
    return (
      <StatusNotification
        iconGlyph="IconFaceSad"
        title="groups_responsible_error_title"
        message="groups_responsible_error_message"
      />
    )
  }

  if (!isLoggedIn) {
    return (
      <StatusNotification
        iconGlyph="IconOnePersonCircle"
        title="groups_responsible_not_login_title"
        message="groups_responsible_not_login_message"
      />
    )
  }

  return (
    <>
      <Hero />
      <TabsWrapper>
        {activeTabs.participants && (
          <TabSection
            title={intl.formatMessage({ id: 'groups_responsible_table_participants' })}
            Component={ParticipantTable}
          />
        )}
        {activeTabs.rooms && (
          <TabSection title={intl.formatMessage({ id: 'groups_responsible_table_rooms' })} Component={RoomsTable} />
        )}
        {activeTabs.bus && (
          <TabSection
            title={intl.formatMessage({ id: 'groups_responsible_table_bus_schedule' })}
            subtitle={intl.formatMessage(
              {
                id: 'groups_responsible_table_bus_assignment_subtitle',
              },
              {
                deadLineDate,
                daysBefore,
              },
            )}
            Component={BusTable}
          />
        )}
      </TabsWrapper>
    </>
  )
}

function LoadingState() {
  return (
    <CenteredContent>
      <LoadingSpinner />
    </CenteredContent>
  )
}

function TabSection({
  title,
  subtitle,
  Component,
}: {
  title: string
  subtitle?: string
  Component: React.ComponentType
}) {
  return (
    <TableWrapper>
      <TableTitleWrapper>
        <TabTitle as="h2" $variant="display-c-regular">
          {title}
        </TabTitle>
        <DownloadButton />
      </TableTitleWrapper>
      {subtitle && <TabSubtitle as="p" $variant="body-m-regular-snug">{subtitle}</TabSubtitle>}
      <Component />
    </TableWrapper>
  )
}
